import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../context/AuthContext';
import { getBlockStatusDetails, saveBlockStatusDetails } from '../services/api';
import StatusDetailAttachments from './StatusDetailAttachments';
import './BlockEditor.css';

const PROCESSING_DELIVERABLES = [
  'Orthomosaic',
  'DSM',
  'DTM',
  'Contour',
  'Spot Elevation',
  '3D point cloud',
  '3D textured mesh'
];

const EMPTY_GCP_FORM = { date: '', no_of_gcps: '', crs: '', surveyor: '', device: '', remarks: '' };
const EMPTY_ACQUISITION_FORM = {
  date: '', start_time: '', end_time: '', pilot: '', drone_used: '', no_of_images: '', gsd: '', remarks: ''
};
const EMPTY_PROCESSING_FORM = { date: '', crs: '', gsd: '', remarks: '', checkboxes: {} };

function BlockEditor({ block, onClose, onUpdate }) {
  const { canEdit } = useAuth();
  const [activeTab, setActiveTab] = useState('gcp');
  const [statusDetails, setStatusDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Form states
  const [gcpForm, setGcpForm] = useState(() => ({ ...EMPTY_GCP_FORM }));
  const [acquisitionForm, setAcquisitionForm] = useState(() => ({ ...EMPTY_ACQUISITION_FORM }));
  const [processingForm, setProcessingForm] = useState(() => ({ ...EMPTY_PROCESSING_FORM }));

  useEffect(() => {
    if (block) {
      fetchStatusDetails();
    }
  }, [block]);

  const fetchStatusDetails = async () => {
    try {
      setLoading(true);
      const details = await getBlockStatusDetails(block.id);
      const detailsMap = {};
      details.forEach(detail => {
        detailsMap[detail.status_type] = detail;
      });
      setStatusDetails(detailsMap);

      // Populate forms with existing data (reset when a stage has no saved row)
      if (detailsMap.gcp) {
        setGcpForm({
          date: detailsMap.gcp.date || '',
          no_of_gcps: detailsMap.gcp.no_of_gcps || '',
          crs: detailsMap.gcp.crs || '',
          surveyor: detailsMap.gcp.surveyor || '',
          device: detailsMap.gcp.device || '',
          remarks: detailsMap.gcp.remarks || ''
        });
      } else {
        setGcpForm({ ...EMPTY_GCP_FORM });
      }
      if (detailsMap.acquisition) {
        setAcquisitionForm({
          date: detailsMap.acquisition.date || '',
          start_time: detailsMap.acquisition.start_time || '',
          end_time: detailsMap.acquisition.end_time || '',
          pilot: detailsMap.acquisition.pilot || '',
          drone_used: detailsMap.acquisition.drone_used || '',
          no_of_images: detailsMap.acquisition.no_of_images || '',
          gsd: detailsMap.acquisition.gsd || '',
          remarks: detailsMap.acquisition.remarks || ''
        });
      } else {
        setAcquisitionForm({ ...EMPTY_ACQUISITION_FORM });
      }
      if (detailsMap.processing) {
        const checkboxes = {};
        if (detailsMap.processing.checkboxes) {
          detailsMap.processing.checkboxes.forEach(cb => {
            checkboxes[cb.checkbox_type] = cb.checked === 1;
          });
        }
        setProcessingForm({
          date: detailsMap.processing.date || '',
          crs: detailsMap.processing.crs || '',
          gsd: detailsMap.processing.gsd || '',
          remarks: detailsMap.processing.remarks || '',
          checkboxes
        });
      } else {
        setProcessingForm({ ...EMPTY_PROCESSING_FORM, checkboxes: {} });
      }
    } catch (err) {
      console.error('Error fetching status details:', err);
    } finally {
      setLoading(false);
    }
  };

  const validateGcpForm = () => {
    return gcpForm.date && gcpForm.no_of_gcps && gcpForm.crs;
  };

  const validateAcquisitionForm = () => {
    return acquisitionForm.date && acquisitionForm.start_time && 
           acquisitionForm.end_time && acquisitionForm.pilot && 
           acquisitionForm.drone_used && acquisitionForm.no_of_images;
  };

  const validateProcessingForm = () => {
    const hasCheckedBox = Object.values(processingForm.checkboxes).some(checked => checked);
    return processingForm.date && processingForm.crs && hasCheckedBox;
  };

  const handleSaveStatus = async (statusType) => {
    if (!canEdit()) {
      setError('You do not have permission to edit blocks');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      let data = {};
      let checkboxes = null;

      if (statusType === 'gcp') {
        if (!validateGcpForm()) {
          setError('Please fill all required fields for GCPs Compilation');
          setSaving(false);
          return;
        }
        data = { ...gcpForm, no_of_gcps: parseInt(gcpForm.no_of_gcps) };
      } else if (statusType === 'acquisition') {
        if (!validateAcquisitionForm()) {
          setError('Please fill all required fields for Drone Acquisition');
          setSaving(false);
          return;
        }
        data = { ...acquisitionForm, no_of_images: parseInt(acquisitionForm.no_of_images) };
      } else if (statusType === 'processing') {
        if (!validateProcessingForm()) {
          setError('Please fill all required fields and select at least one deliverable for Data Processing');
          setSaving(false);
          return;
        }
        checkboxes = PROCESSING_DELIVERABLES.map(type => ({
          type,
          checked: processingForm.checkboxes[type] || false
        })).filter(cb => cb.checked);
        data = { ...processingForm, checkboxes };
      }

      await saveBlockStatusDetails(block.id, statusType, data);
      
      // Note: Backend automatically updates block status when saving status details
      // No need to call updateBlockStatus separately

      await fetchStatusDetails();
      if (onUpdate) {
        onUpdate();
      }
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to save status details');
    } finally {
      setSaving(false);
    }
  };

  const handleClearAndSave = async (statusType) => {
    if (!canEdit()) {
      setError('You do not have permission to edit blocks');
      return;
    }
    if (
      !window.confirm(
        'Remove all saved details for this stage on this block? The checklist status for this stage will be cleared.'
      )
    ) {
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await saveBlockStatusDetails(block.id, statusType, { clear: true });
      await fetchStatusDetails();
      if (onUpdate) {
        onUpdate();
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to clear status details');
    } finally {
      setSaving(false);
    }
  };

  if (!block || !canEdit()) {
    return null;
  }

  const modalContent = (
    <div className="block-editor-overlay" onClick={onClose}>
      {loading ? (
        <div className="block-editor-modal" onClick={(e) => e.stopPropagation()}>
          <div className="loading">Loading...</div>
        </div>
      ) : (
        <div className="block-editor-modal enhanced" onClick={(e) => e.stopPropagation()}>
          <div className="block-editor-header">
            <h3>Edit Block Status Details</h3>
            <button className="close-button" onClick={onClose}>×</button>
          </div>

          <div className="status-tabs">
            <button 
              className={`tab ${activeTab === 'gcp' ? 'active' : ''}`}
              onClick={() => setActiveTab('gcp')}
            >
              GCPs Compilation
              {statusDetails.gcp && <span className="status-indicator">✓</span>}
            </button>
            <button 
              className={`tab ${activeTab === 'acquisition' ? 'active' : ''}`}
              onClick={() => setActiveTab('acquisition')}
            >
              Drone Acquisition
              {statusDetails.acquisition && <span className="status-indicator">✓</span>}
            </button>
            <button 
              className={`tab ${activeTab === 'processing' ? 'active' : ''}`}
              onClick={() => setActiveTab('processing')}
            >
              Data Processing
              {statusDetails.processing && <span className="status-indicator">✓</span>}
            </button>
          </div>

          <div className="block-editor-content">
            {activeTab === 'gcp' && (
              <GcpForm 
                form={gcpForm} 
                setForm={setGcpForm}
                blockId={block.id}
                attachments={statusDetails.gcp?.attachments || []}
                onAttachmentsChange={fetchStatusDetails}
                onSave={() => handleSaveStatus('gcp')}
                onClearSave={() => handleClearAndSave('gcp')}
                hasSavedDetail={!!statusDetails.gcp}
                saving={saving}
              />
            )}

            {activeTab === 'acquisition' && (
              <AcquisitionForm 
                form={acquisitionForm} 
                setForm={setAcquisitionForm}
                blockId={block.id}
                attachments={statusDetails.acquisition?.attachments || []}
                onAttachmentsChange={fetchStatusDetails}
                onSave={() => handleSaveStatus('acquisition')}
                onClearSave={() => handleClearAndSave('acquisition')}
                hasSavedDetail={!!statusDetails.acquisition}
                saving={saving}
              />
            )}

            {activeTab === 'processing' && (
              <ProcessingForm 
                form={processingForm} 
                setForm={setProcessingForm}
                blockId={block.id}
                attachments={statusDetails.processing?.attachments || []}
                onAttachmentsChange={fetchStatusDetails}
                onSave={() => handleSaveStatus('processing')}
                onClearSave={() => handleClearAndSave('processing')}
                hasSavedDetail={!!statusDetails.processing}
                saving={saving}
              />
            )}

            {error && <div className="error-message">{error}</div>}

            <div className="block-editor-actions">
              <button 
                className="cancel-button" 
                onClick={onClose}
                disabled={saving}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return createPortal(modalContent, document.body);
}

function GcpForm({
  form, setForm, blockId, attachments, onAttachmentsChange,
  onSave, onClearSave, hasSavedDetail, saving,
}) {
  const isValid = form.date && form.no_of_gcps && form.crs;

  return (
    <div className="status-form">
      <h4>GCPs Compilation</h4>
      <div className="form-group">
        <label>Date <span className="required">*</span></label>
        <input
          type="date"
          value={form.date}
          onChange={(e) => setForm({ ...form, date: e.target.value })}
          disabled={saving}
          required
        />
      </div>
      <div className="form-group">
        <label>No of GCPs <span className="required">*</span></label>
        <input
          type="number"
          min="1"
          value={form.no_of_gcps}
          onChange={(e) => setForm({ ...form, no_of_gcps: e.target.value })}
          disabled={saving}
          required
        />
      </div>
      <div className="form-group">
        <label>CRS <span className="required">*</span></label>
        <input
          type="text"
          value={form.crs}
          onChange={(e) => setForm({ ...form, crs: e.target.value })}
          disabled={saving}
          required
        />
      </div>
      <div className="form-group">
        <label>Surveyor</label>
        <input
          type="text"
          value={form.surveyor}
          onChange={(e) => setForm({ ...form, surveyor: e.target.value })}
          disabled={saving}
        />
      </div>
      <div className="form-group">
        <label>Device</label>
        <input
          type="text"
          value={form.device}
          onChange={(e) => setForm({ ...form, device: e.target.value })}
          disabled={saving}
        />
      </div>
      <StatusDetailAttachments
        blockId={blockId}
        statusType="gcp"
        attachments={attachments}
        hasSavedDetail={hasSavedDetail}
        onAttachmentsChange={onAttachmentsChange}
        disabled={saving}
      />
      <div className="form-group">
        <label>Remarks</label>
        <textarea
          value={form.remarks}
          onChange={(e) => setForm({ ...form, remarks: e.target.value })}
          disabled={saving}
          rows="3"
        />
      </div>
      <div className="status-form-actions">
        <button
          className="save-button"
          type="button"
          onClick={onSave}
          disabled={saving || !isValid}
        >
          {saving ? 'Saving...' : 'Save GCPs Status'}
        </button>
        {hasSavedDetail && (
          <button
            className="clear-save-button"
            type="button"
            onClick={onClearSave}
            disabled={saving}
          >
            Clear &amp; save
          </button>
        )}
      </div>
    </div>
  );
}

function AcquisitionForm({
  form, setForm, blockId, attachments, onAttachmentsChange,
  onSave, onClearSave, hasSavedDetail, saving,
}) {
  const isValid = form.date && form.start_time && form.end_time && 
                  form.pilot && form.drone_used && form.no_of_images;

  return (
    <div className="status-form">
      <h4>Drone Acquisition</h4>
      <div className="form-group">
        <label>Date <span className="required">*</span></label>
        <input
          type="date"
          value={form.date}
          onChange={(e) => setForm({ ...form, date: e.target.value })}
          disabled={saving}
          required
        />
      </div>
      <div className="form-row">
        <div className="form-group">
          <label>Start Time <span className="required">*</span></label>
          <input
            type="time"
            value={form.start_time}
            onChange={(e) => setForm({ ...form, start_time: e.target.value })}
            disabled={saving}
            required
          />
        </div>
        <div className="form-group">
          <label>End Time <span className="required">*</span></label>
          <input
            type="time"
            value={form.end_time}
            onChange={(e) => setForm({ ...form, end_time: e.target.value })}
            disabled={saving}
            required
          />
        </div>
      </div>
      <div className="form-group">
        <label>Pilot <span className="required">*</span></label>
        <input
          type="text"
          value={form.pilot}
          onChange={(e) => setForm({ ...form, pilot: e.target.value })}
          disabled={saving}
          required
        />
      </div>
      <div className="form-group">
        <label>Drone Used <span className="required">*</span></label>
        <input
          type="text"
          value={form.drone_used}
          onChange={(e) => setForm({ ...form, drone_used: e.target.value })}
          disabled={saving}
          required
        />
      </div>
      <div className="form-group">
        <label>No of Images <span className="required">*</span></label>
        <input
          type="number"
          min="1"
          value={form.no_of_images}
          onChange={(e) => setForm({ ...form, no_of_images: e.target.value })}
          disabled={saving}
          required
        />
      </div>
      <div className="form-group">
        <label>GSD</label>
        <input
          type="text"
          value={form.gsd}
          onChange={(e) => setForm({ ...form, gsd: e.target.value })}
          disabled={saving}
          placeholder="e.g., 5 cm/pixel"
        />
      </div>
      <StatusDetailAttachments
        blockId={blockId}
        statusType="acquisition"
        attachments={attachments}
        hasSavedDetail={hasSavedDetail}
        onAttachmentsChange={onAttachmentsChange}
        disabled={saving}
      />
      <div className="form-group">
        <label>Remarks</label>
        <textarea
          value={form.remarks}
          onChange={(e) => setForm({ ...form, remarks: e.target.value })}
          disabled={saving}
          rows="3"
        />
      </div>
      <div className="status-form-actions">
        <button
          className="save-button"
          type="button"
          onClick={onSave}
          disabled={saving || !isValid}
        >
          {saving ? 'Saving...' : 'Save Acquisition Status'}
        </button>
        {hasSavedDetail && (
          <button
            className="clear-save-button"
            type="button"
            onClick={onClearSave}
            disabled={saving}
          >
            Clear &amp; save
          </button>
        )}
      </div>
    </div>
  );
}

function ProcessingForm({
  form, setForm, blockId, attachments, onAttachmentsChange,
  onSave, onClearSave, hasSavedDetail, saving,
}) {
  const hasCheckedBox = Object.values(form.checkboxes).some(checked => checked);
  const isValid = form.date && form.crs && hasCheckedBox;

  const handleCheckboxChange = (type, checked) => {
    setForm({
      ...form,
      checkboxes: { ...form.checkboxes, [type]: checked }
    });
  };

  return (
    <div className="status-form">
      <h4>Data Processing</h4>
      <div className="form-group">
        <label>Date <span className="required">*</span></label>
        <input
          type="date"
          value={form.date}
          onChange={(e) => setForm({ ...form, date: e.target.value })}
          disabled={saving}
          required
        />
      </div>
      <div className="form-group">
        <label>CRS <span className="required">*</span></label>
        <input
          type="text"
          value={form.crs}
          onChange={(e) => setForm({ ...form, crs: e.target.value })}
          disabled={saving}
          required
        />
      </div>
      <div className="form-group">
        <label>GSD</label>
        <input
          type="text"
          value={form.gsd}
          onChange={(e) => setForm({ ...form, gsd: e.target.value })}
          disabled={saving}
          placeholder="e.g., 5 cm/pixel"
        />
      </div>
      <div className="form-group">
        <label>Processing Deliverables <span className="required">*</span> (Select at least one)</label>
        <div className="checkboxes-group">
          {PROCESSING_DELIVERABLES.map(type => (
            <label key={type} className="checkbox-label">
              <input
                type="checkbox"
                checked={form.checkboxes[type] || false}
                onChange={(e) => handleCheckboxChange(type, e.target.checked)}
                disabled={saving}
              />
              <span>{type}</span>
            </label>
          ))}
        </div>
      </div>
      <StatusDetailAttachments
        blockId={blockId}
        statusType="processing"
        attachments={attachments}
        hasSavedDetail={hasSavedDetail}
        onAttachmentsChange={onAttachmentsChange}
        disabled={saving}
      />
      <div className="form-group">
        <label>Remarks</label>
        <textarea
          value={form.remarks}
          onChange={(e) => setForm({ ...form, remarks: e.target.value })}
          disabled={saving}
          rows="3"
        />
      </div>
      <div className="status-form-actions">
        <button
          className="save-button"
          type="button"
          onClick={onSave}
          disabled={saving || !isValid}
        >
          {saving ? 'Saving...' : 'Save Processing Status'}
        </button>
        {hasSavedDetail && (
          <button
            className="clear-save-button"
            type="button"
            onClick={onClearSave}
            disabled={saving}
          >
            Clear &amp; save
          </button>
        )}
      </div>
    </div>
  );
}

export default BlockEditor;
